import { useCallback, useContext, useEffect, useMemo, useState } from "react";
import PropTypes from "prop-types";
import { PropertyStoreContext } from "../../contexts/PropertyStoreContext";
import usePropertyPickerOptions from "../../hooks/usePropertyPickerOptions";
import {
  Button,
  ButtonToolbar,
  Col,
  Form,
  Row,
  SelectPicker,
  DatePicker,
} from "rsuite";
import { toast } from "react-toastify";
import TagPicker from "../common/tagPicker/TagPicker";
import MultiDateRangePicker from "../common/multiDateRangePicker/MultiDateRangePicker";
import CloseIcon from "@rsuite/icons/Close";
import { beforeToday } from "rsuite/cjs/DateRangePicker/disabledDateUtils";
import { Can, P } from "../../rbac";
import { formatPropertyLabel } from "../../utils/propertyLabel";
import "./priceCheckForm.scss";
import {
  getInitialFormValues,
  persistFormValues,
  validateFormValues,
  getValidationMessages,
  clearValidationErrors as clearValidationErrorsUtil,
  generateAgeOptions,
  generateCityOptions,
  updateChildAges,
  MAX_STAY_NIGHTS,
  getPastCheckInConflicts,
  getPastCheckInMessages,
  getPastBookDateConflicts,
  getPastBookDateMessages,
} from "./PriceCheckFormUtils";

const PriceCheckForm = ({
  onSubmit,
  onClose,
  source = null,
  formValues: propFormValues,
  setFormValues: propSetFormValues,
  properties: propProperties,
}) => {
  const { properties: contextProperties } = useContext(PropertyStoreContext);
  const [cities, setCities] = useState([]);
  const [validationErrors, setValidationErrors] = useState({});

  const [formValues, setFormValues] = useState(
    getInitialFormValues(contextProperties)
  );
  const finalFormValues = propFormValues || formValues;
  const finalSetFormValues = propSetFormValues || setFormValues;
  const finalProperties = propProperties || contextProperties;

  // Server-side searchable property options (the picker shows 20 at a time and
  // searches by name through the API, like the header property selector).
  // `propertyName` stays untouched — downstream Price Check code matches rows
  // by name — so the code is carried on a separate label field for the picker.
  const propertyToOption = useCallback(
    (property) => ({
      ...property,
      propertyLabel: formatPropertyLabel(property),
    }),
    []
  );
  const getPropertyValue = useCallback((property) => property?.propertyId, []);
  const {
    options: propertyOptions,
    onSearch: onPropertiesSearch,
    reset: resetPropertyOptions,
  } = usePropertyPickerOptions({
    selectedValues: finalFormValues.selectedProperties,
    toOption: propertyToOption,
    getOptionValue: getPropertyValue,
    seedProperties: finalProperties,
  });

  // Generate age options for child age dropdowns (memoized)
  const ageOptions = useMemo(() => generateAgeOptions(), []);

  useEffect(() => {
    setCities(generateCityOptions(finalProperties));
  }, [finalProperties]);

  // Update child ages when children count changes
  useEffect(() => {
    const currentChildAges = finalFormValues.childAges || [];
    const newChildAges = updateChildAges(
      currentChildAges,
      finalFormValues.children
    );

    if (newChildAges.length !== currentChildAges.length) {
      finalSetFormValues((prev) => ({ ...prev, childAges: newChildAges }));
    }
  }, [finalFormValues.children, finalFormValues.childAges, finalSetFormValues]);

  const handleChange = (key, value) => {
    finalSetFormValues((prev) => {
      const newFormValues = { ...prev, [key]: value };

      // Clear validation errors immediately when user makes changes
      const errorsToClear = clearValidationErrorsUtil(
        newFormValues,
        key,
        validationErrors
      );
      if (errorsToClear) {
        setValidationErrors((prev) => ({ ...prev, ...errorsToClear }));
      }

      return newFormValues;
    });
  };

  // Handle external form value changes (for controlled components)
  useEffect(() => {
    // Only run if there are active validation errors
    if (Object.values(validationErrors).some(Boolean)) {
      const errorsToClear = clearValidationErrorsUtil(
        finalFormValues,
        null,
        validationErrors
      );
      if (errorsToClear) {
        setValidationErrors((prev) => ({ ...prev, ...errorsToClear }));
      }
    }
  }, [finalFormValues, validationErrors]);

  const handleChildAgeChange = (index, value) => {
    finalSetFormValues((prev) => ({
      ...prev,
      childAges: prev.childAges.map((age, i) => (i === index ? value : age)),
    }));
  };

  const handleFormSubmit = (e) => {
    if (e && typeof e.preventDefault === "function") {
      e.preventDefault();
    }

    // Reset validation errors
    setValidationErrors({});

    // Validate form values
    const newValidationErrors = validateFormValues(finalFormValues);
    const validationMessages = getValidationMessages(newValidationErrors);

    // Show error messages
    validationMessages.forEach((message) => toast.error(message));

    // If there are validation errors, set them and return
    if (Object.keys(newValidationErrors).length > 0) {
      setValidationErrors(newValidationErrors);
      return;
    }

    // Attach the full selected property objects (name + expediaHotelId) captured
    // from the server-side picker, so downstream resolution doesn't depend on
    // the first-page context list.
    const selectedPropertyObjects = (finalFormValues.selectedProperties || [])
      .map((id) => propertyOptions.find((p) => p?.propertyId === id))
      .filter(Boolean);

    // EO-1137 — a check-in already past at one of the chosen properties.
    //
    // ⚠ Must come BEFORE onSubmit. onSubmit is what tears this form down: the drawer
    // caller closes it (setIsDrawerOpen(false)) and the inline caller leaves edit mode.
    // Validating after it would show the toast against a form the user can no longer
    // see or correct — they would have to reopen it and re-enter the search to find out
    // what was wrong. Returning here leaves everything exactly as they left it.
    //
    // The same check also runs in usePriceCheckSearch, which is not redundant: the
    // auto-search replay and tab switches reach the API without ever passing through
    // this form. This copy exists so the form can stay open; that one is the backstop.
    //
    // Only properties that actually carry a zone are judged — see getPastCheckInConflicts.
    const zonedProperties = selectedPropertyObjects.filter((p) => p?.timeZone);

    const pastCheckInConflicts = getPastCheckInConflicts(
      zonedProperties,
      finalFormValues.dateRanges || []
    );
    // The booking creation date has the same blind spot — its picker and the stored-value
    // guard both measure "past" against the user's clock, not the property's.
    const pastBookDateConflicts = getPastBookDateConflicts(
      zonedProperties,
      finalFormValues.bookDate
    );
    if (pastCheckInConflicts.length > 0 || pastBookDateConflicts.length > 0) {
      [
        ...getPastCheckInMessages(pastCheckInConflicts),
        ...getPastBookDateMessages(pastBookDateConflicts),
      ].forEach((message) => toast.error(message));
      return;
    }

    persistFormValues(finalFormValues);

    onSubmit({ ...finalFormValues, selectedPropertyObjects });
    if (source === "drawer") {
      onClose();
    }
  };

  return (
    <div className="price-check-search-form-container">
      <div className="form-header">
        <p className="page-header">Price Check</p>
        <div className="close-button">
          {source === "drawer" && (
            <Button
              appearance="ghost"
              onClick={onClose}
              endIcon={<CloseIcon />}
            >
              Close
            </Button>
          )}
        </div>
      </div>

      <Form fluid onSubmit={handleFormSubmit} formValue={finalFormValues}>
        <Row gutter={32}>
          <Col md={8}>
            <Form.Group controlId="property">
              <Form.ControlLabel>
                Property
                {finalFormValues?.selectedCity?.length ? null : (
                  <span className="required-field">*</span>
                )}
              </Form.ControlLabel>
              <TagPicker
                block
                data={propertyOptions}
                labelKey="propertyLabel"
                valueKey="propertyId"
                placeholder="Choose properties"
                value={finalFormValues.selectedProperties}
                onChange={(val) => handleChange("selectedProperties", val)}
                onSearch={onPropertiesSearch}
                onSelect={resetPropertyOptions}
                cleanable={false}
                disabled={
                  finalFormValues.selectedCity &&
                  finalFormValues.selectedCity.length > 0
                }
                className={validationErrors.property ? "validation-error" : ""}
              />
            </Form.Group>
          </Col>
          <Col md={6}>
            <Form.Group controlId="city">
              <Form.ControlLabel>
                City
                {finalFormValues?.selectedProperties?.length ? null : (
                  <span className="required-field">*</span>
                )}
              </Form.ControlLabel>
              <TagPicker
                block
                data={cities}
                placeholder="Choose city"
                value={finalFormValues.selectedCity}
                onChange={(val) => handleChange("selectedCity", val)}
                cleanable={false}
                disabled={
                  finalFormValues.selectedProperties &&
                  finalFormValues.selectedProperties.length > 0
                }
                className={validationErrors.city ? "validation-error" : ""}
              />
            </Form.Group>
          </Col>
          <Col md={8}>
            <Form.Group controlId="stayDates">
              <Form.ControlLabel>
                Stay dates <span className="required-field">*</span>
              </Form.ControlLabel>
              <MultiDateRangePicker
                dateRanges={finalFormValues.dateRanges}
                setDateRanges={(val) => handleChange("dateRanges", val)}
                // Same constant validateFormValues enforces, so the calendar and the
                // submit check can never disagree about what counts as too long.
                maxNights={MAX_STAY_NIGHTS}
                isRequired={true}
                hasError={
                  !finalFormValues.dateRanges ||
                  finalFormValues.dateRanges.length === 0
                }
                className={
                  validationErrors.dateRanges ? "validation-error" : ""
                }
              />
            </Form.Group>
          </Col>
        </Row>
        <Row gutter={32}>
          <Col md={4}>
            <Form.Group controlId="bookDate">
              <Form.ControlLabel>Booking Creation Date</Form.ControlLabel>
              <DatePicker
                name="bookDate"
                value={finalFormValues.bookDate}
                onChange={(val) => handleChange("bookDate", val)}
                placeholder="Choose dates"
                className="booking-date-input"
                format="MMM d, yyyy"
                shouldDisableDate={beforeToday()}
                block
                container={() => document.body}
                placement="autoVerticalStart"
              />
            </Form.Group>
          </Col>
          <Col md={4}>
            <div className="form-group-with-divider">
              <div className="vertical-divider"></div>
              <Form.Group controlId="adults">
                <Form.ControlLabel>Number of adults</Form.ControlLabel>
                <div className="number-input-group">
                  <Button
                    className="number-btn"
                    onClick={() =>
                      handleChange(
                        "adults",
                        Math.max(1, finalFormValues.adults - 1)
                      )
                    }
                  >
                    -
                  </Button>
                  <div className="number-display">{finalFormValues.adults}</div>
                  <Button
                    className="number-btn"
                    onClick={() =>
                      handleChange(
                        "adults",
                        Math.min(10, finalFormValues.adults + 1)
                      )
                    }
                  >
                    +
                  </Button>
                </div>
              </Form.Group>
            </div>
          </Col>
          <Col md={4}>
            <div className="form-group-with-divider">
              <div className="vertical-divider"></div>
              <Form.Group controlId="children">
                <Form.ControlLabel>Number of children</Form.ControlLabel>
                <div className="number-input-group">
                  <Button
                    className="number-btn"
                    onClick={() =>
                      handleChange(
                        "children",
                        Math.max(0, finalFormValues.children - 1)
                      )
                    }
                  >
                    -
                  </Button>
                  <div className="number-display">
                    {finalFormValues.children}
                  </div>
                  <Button
                    className="number-btn"
                    onClick={() =>
                      handleChange(
                        "children",
                        Math.min(8, finalFormValues.children + 1)
                      )
                    }
                  >
                    +
                  </Button>
                </div>
              </Form.Group>
            </div>
          </Col>
          {finalFormValues.children > 0 && (
            <Col md={12}>
              <Form.Group controlId="childAges">
                <div className="child-ages-container">
                  {Array.from(
                    { length: finalFormValues.children },
                    (_, index) => (
                      <div key={index} className="child-age-item">
                        <span className="rs-form-control-label">
                          Age of child {index + 1}
                        </span>
                        <SelectPicker
                          name={`childAges-[${index}]`}
                          data={ageOptions}
                          searchable={false}
                          cleanable={false}
                          placeholder="Age"
                          value={finalFormValues.childAges[index] || 0}
                          onChange={(val) => handleChildAgeChange(index, val)}
                          className="child-age-picker"
                          menuClassName="child-age-menu"
                        />
                      </div>
                    )
                  )}
                </div>
              </Form.Group>
            </Col>
          )}
        </Row>
        <Row className="mx-0">
          <Form.Group>
            <ButtonToolbar>
              <Can permission={P.PriceCheck.Search}>
                <Button type="submit" appearance="primary" size="lg">
                  Search
                </Button>
              </Can>
            </ButtonToolbar>
          </Form.Group>
        </Row>
      </Form>
    </div>
  );
};

PriceCheckForm.propTypes = {
  onSubmit: PropTypes.func.isRequired,
  onClose: PropTypes.func.isRequired,
  source: PropTypes.string,
  formValues: PropTypes.shape({
    selectedProperties: PropTypes.arrayOf(PropTypes.string),
    selectedCity: PropTypes.arrayOf(PropTypes.string),
    dateRanges: PropTypes.arrayOf(
      PropTypes.oneOfType([PropTypes.string, PropTypes.object])
    ),
    bookDate: PropTypes.oneOfType([
      PropTypes.instanceOf(Date),
      PropTypes.string,
      PropTypes.oneOf([null]),
    ]),
    adults: PropTypes.number,
    children: PropTypes.number,
    childAges: PropTypes.arrayOf(PropTypes.number),
  }),
  setFormValues: PropTypes.func,
  properties: PropTypes.arrayOf(
    PropTypes.shape({
      propertyId: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
        .isRequired,
      propertyName: PropTypes.string.isRequired,
      propertyCode: PropTypes.string,
      city: PropTypes.string.isRequired,
    })
  ),
};

export default PriceCheckForm;
